import { Component, OnInit, OnDestroy } from '@angular/core';
import { GenericService } from 'src/app/services/generic.service';
declare const L: any;

@Component({
  selector: 'app-user-live-tracking',
  templateUrl: './user-live-tracking.component.html',
  styleUrls: ['./user-live-tracking.component.css']
})
export class UserLiveTrackingComponent implements OnInit, OnDestroy {
  private map!: L.Map;
  private centroid: L.LatLngExpression = [17.3850, 78.4867]; // Hyderabad roughly

  usersList: any[] = [];
  searchQuery: string = '';
  selectedUser: any = null;
  userMarkers: { [key: string]: L.Marker } = {}; // To keep track of user markers

  constructor(private genericService: GenericService) { }

  ngOnInit(): void {
    this.initMap();
    this.getUsers();
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }

  get filteredUsers() {
    if (!this.searchQuery) return this.usersList;
    const lowerQuery = this.searchQuery.toLowerCase();
    return this.usersList.filter(user =>
      (user.firstName || user.username || '').toLowerCase().includes(lowerQuery) ||
      (user.email || '').toLowerCase().includes(lowerQuery)
    );
  }

  private initMap(): void {
    this.map = L.map('live-tracking-map', {
      center: this.centroid,
      zoom: 12
    });

    const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap'
    });

    tiles.addTo(this.map);
  }

  getUsers(): void {
    // Reusing the method to get users that can be tracked
    this.genericService.getUsersWithoutCustomers().subscribe(
      (res: any) => {
        if (res && res.response) {
          // Filter out users that have usertracking=true if needed, or just take them all
          let list: any[] = [];
          for (let user of res.response) {
            if (user.keyValues && user.keyValues.length > 0) {
              let canTrack = false;
              for (let kv of user.keyValues) {
                if (kv.key.toLowerCase() === "usertracking" && kv.value.toLowerCase() === "true") {
                  canTrack = true;
                  break;
                }
              }
              if (canTrack) {
                list.push(user);
              }
            }
          }
          // If no specific tracking key, just use all for the demo
          this.usersList = list.length > 0 ? list : res.response;

          this.plotMockUserLocations();
        }
      },
      (error: any) => {
        console.error('Error fetching users', error);
      }
    );
  }

  // Plot some mock locations around the centroid for the users
  plotMockUserLocations(): void {
    this.usersList.forEach((user, index) => {
      // Create a slight offset for each user so they don't overlap exactly
      const latOffset = (Math.random() - 0.5) * 0.1;
      const lngOffset = (Math.random() - 0.5) * 0.1;
      const userLat = (this.centroid as any)[0] + latOffset;
      const userLng = (this.centroid as any)[1] + lngOffset;

      user.lastKnownLocation = { lat: userLat, lng: userLng };

      const userImage = user.profilePicture || 'assets/img/dummy-image.jpeg';

      const customHtmlIcon = this.getMarkerIcon(userImage, false);

      const marker = L.marker([userLat, userLng], { icon: customHtmlIcon }).addTo(this.map);
      // marker.bindPopup(`<b>${user.firstName || user.username || 'User'}</b><br>${user.email || ''}`);

      this.userMarkers[user.id || index] = marker;
    });
  }

  selectUser(user: any, index: number): void {
    this.selectedUser = user;

    // Reset all markers
    this.usersList.forEach((u, i) => {
      const uImage = u.profilePicture || 'assets/img/dummy-image.jpeg';
      const mKey = u.id || i;
      if (this.userMarkers[mKey]) {
        this.userMarkers[mKey].setIcon(this.getMarkerIcon(uImage, false));
      }
    });

    // Update selected marker
    const selectedImage = user.profilePicture || 'assets/img/dummy-image.jpeg';
    const markerKey = user.id || index;
    if (this.userMarkers[markerKey]) {
      this.userMarkers[markerKey].setIcon(this.getMarkerIcon(selectedImage, true));
    }

    if (user.lastKnownLocation) {
      this.map.setView([user.lastKnownLocation.lat, user.lastKnownLocation.lng], 15);

      if (this.userMarkers[markerKey]) {
        this.userMarkers[markerKey].openPopup();
      }
    }
  }

  getMarkerIcon(userImage: string, isSelected: boolean): any {
    const borderColor = isSelected ? '#007bff' : '#c0c0c0';
    const borderWidth = isSelected ? '4px' : '2px';
    const size = isSelected ? 55 : 45;
    const arrowBorder = isSelected ? 10 : 8;
    const arrowBottom = isSelected ? -8 : -6;

    return L.divIcon({
      className: '',
      html: `
        <div style="
          position: relative;
          width: ${size}px;
          height: ${size}px;
          background: white;
          border: ${borderWidth} solid ${borderColor};
          border-radius: 50%;
          overflow: hidden;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          z-index: 2;
        ">
          <img src="${userImage}" onerror="this.src='assets/img/dummy-image.jpeg'" style="width: 100%; height: 100%; object-fit: cover;" />
        </div>
        <div style="
          position: absolute;
          bottom: ${arrowBottom}px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: ${arrowBorder - 2}px solid transparent;
          border-right: ${arrowBorder - 2}px solid transparent;
          border-top: ${arrowBorder}px solid ${borderColor};
          z-index: 1;
        "></div>
      `,
      iconAnchor: [size / 2, size + arrowBorder],
      popupAnchor: [0, -(size + arrowBorder)]
    });
  }
}
